'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { CallToActionCard } from '@benzinga/core-ui';
import { QuotePriceFull } from '../QuotePrice';
import { DelayedQuote, QuoteSessionType } from '@benzinga/quotes-manager';
import QuoteBuyButton from '../StockQuote/QuoteBuyButton';
import QuoteSellButton from '../StockQuote/QuoteSellButton';
import CompareBrokersButton from '../StockQuote/CompareBrokersButton';
import QuoteExchangeInformation from '../StockQuote/QuoteExchangeInformation';
import { StockFundamentals } from '../Fundamentals/StockFundamentals';
import { TickerLink } from '@benzinga/ticker-ui';
import { AddToWatchlist } from '@benzinga/watchlist-ui';
import Hooks from '@benzinga/hooks';

export interface QuoteCardProps {
  quote: DelayedQuote;
  sessionType?: QuoteSessionType;
  initialColumns?: number;
}

export const QuoteCard: React.FC<QuoteCardProps> = ({ initialColumns, quote, sessionType }) => {
  const [ref, { width }] = Hooks.useElementSize();
  const [columns, setColumns] = React.useState(initialColumns ?? 3);

  React.useEffect(() => {
    if (width > 0) {
      setColumns(width < 600 ? 1 : width < 700 ? 2 : 3);
    }
  }, [width]);

  const stockFundamentalsProps = {
    hideDataCell: [4],
  };

  return (
    <Container className="quote-card-container" ref={ref}>
      <div className="quote-name-exchange-container">
        <TickerLink className="ticker-link-quote-name" symbol={quote.symbol}>
          <span className="quote-name">{quote.name}</span>
        </TickerLink>
        <TickerLink className="ticker-link-quote-name" symbol={quote.symbol}>
          <QuoteExchangeInformation exchange={quote.bzExchange} otcTier={quote.otcTier} symbol={quote.symbol} />
        </TickerLink>
        <AddToWatchlist symbol={quote.symbol} variant="compact" />
      </div>
      <QuotePriceFull quote={quote} sessionType={sessionType} />
      <StockFundamentals
        columns={columns}
        labels={{ dividend: 'Div / Yield' }}
        stock={quote}
        {...(columns === 3 ? stockFundamentalsProps : {})}
      />
      <div className="quote-card-buttons-container">
        <QuoteBuyButton />
        <QuoteSellButton />
        <CompareBrokersButton />
      </div>
    </Container>
  );
};

const Container = styled(CallToActionCard)`
  &.quote-card-container {
    width: 100%;
    .quote-name-exchange-container {
      display: flex;
      align-items: center;

      .quote-name {
        font-size: 24px;
        font-weight: ${({ theme }) => theme.fontWeight.semibold};
        color: ${({ theme }) => theme.colorPalette.gray700};
      }

      .quote-exchange-information {
        margin-left: 5px;
        margin-right: 8px;
        color: ${({ theme }) => theme.colorPalette.gray800};
      }
    }

    .quote-value-container {
      .quote-numbers {
        display: flex;
      }
    }

    .quote-card-buttons-container {
      display: flex;
      width: 100%;
      gap: 0.5rem;
    }
    .stock-fundamentals,
    .crypto-fundamentals {
      margin: 1rem 0;
    }

    @media (min-width: 400px) {
      .quote-card-buttons-container {
        a {
          height: 100%;
          width: 50%;
        }
      }
    }

    @media (max-width: 400px) {
      > .quote-card-buttons-container {
        flex-direction: column;
      }

      .buy-sell-buttons-container {
        flex-direction: column;
      }
    }
  }
`;
